import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Trash2, Edit } from "lucide-react";
import { notifications } from "@mantine/notifications";
import { Endpoints } from "../../../../lib/endpoints";
import { PerformRequest } from "../../../../lib/usePerformRequest";
import { InlineLoader } from "../../../../components/Loader/LoadingOverlay";
import { ButtonLoader } from "../../../../components/Loader";

interface Deal {
  id: string;
  location: string;
  transaction_type: string;
  property_value: string;
  commission: string;
  property_type: string;
  date_created: string;
  status: string;
}

export default function DealDetail() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [isLoading, setIsLoading] = useState(true);
  const [deal, setDeal] = useState<Deal | null>(null);

  useEffect(() => {
    if (id) {
      fetchDealDetail();
    }
  }, [id]);

  async function fetchDealDetail() {
    if (!id) return;

    setIsLoading(true);
    try {
      const endpoint = Endpoints.GetDealDetail(id);
      const response = await PerformRequest<Deal>({
        method: endpoint.method,
        route: endpoint.route,
      });

      if (response.status === 200 && response.data) {
        setDeal(response.data);
      } else {
        notifications.show({
          title: "Error",
          message: "Failed to load deal details. Please try again.",
          color: "red",
        });
      }
    } catch (error: any) {
      notifications.show({
        title: "Error",
        message: "Failed to load deal details. Please try again.",
        color: "red",
      });
    } finally {
      setIsLoading(false);
    }
  }

  async function handleDeleteDeal() {
    if (!id) return;

    if (!window.confirm("Are you sure you want to delete this deal? This action cannot be undone.")) {
      return;
    }

    try {
      const endpoint = Endpoints.DeleteDeal(id);
      const response = await PerformRequest({
        method: endpoint.method,
        route: endpoint.route,
      });

      if (response.status === 200 || response.status === 204) {
        notifications.show({
          title: "Success",
          message: "Deal deleted successfully",
          color: "green",
        });
        navigate("/d/deals");
      } else {
        notifications.show({
          title: "Error",
          message: response.message || "Failed to delete deal. Please try again.",
          color: "red",
        });
      }
    } catch (error: any) {
      notifications.show({
        title: "Error",
        message: error?.message || "Failed to delete deal. Please try again.",
        color: "red",
      });
    }
  }

  if (isLoading) {
    return (
      <div className="dashboard-page">
        <div className="dashboard-page__header">
          <button
            onClick={() => navigate("/d/deals")}
            className="dashboard-page__back-button"
            type="button"
            aria-label="Go back"
          >
            <ArrowLeft size={20} />
            <span>Deals Details</span>
          </button>
        </div>
        <InlineLoader isLoading={true} text="Loading deal details..." />
      </div>
    );
  }

  if (!deal) {
    return (
      <div className="dashboard-page">
        <div className="dashboard-page__header">
          <button
            onClick={() => navigate("/d/deals")}
            className="dashboard-page__back-button"
            type="button"
            aria-label="Go back"
          >
            <ArrowLeft size={20} />
            <span>Deals Details</span>
          </button>
        </div>
        <div className="dashboard-empty">Deal not found</div>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <div className="dashboard-page__header">
        <button
          onClick={() => navigate("/d/deals")}
          className="dashboard-page__back-button"
          type="button"
          aria-label="Go back"
        >
          <ArrowLeft size={20} />
          <span>Deals Details</span>
        </button>
      </div>

      <section className="dashboard-panel shadow-[var(--card-shadow)] rounded-2xl">
        <div className="dashboard-panel__header">
          <h2 className="dashboard-panel__title">Deal Information</h2>
          <div style={{ display: "flex", gap: "8px" }}>
            <button
              onClick={handleDeleteDeal}
              className="dashboard-button dashboard-button--danger"
              type="button"
              disabled={isLoading}
              style={{ display: "flex", alignItems: "center", gap: "6px" }}
            >
              <Trash2 size={16} />
              <span>Delete</span>
            </button>
          </div>
        </div>

        <div className="dashboard-detail-grid">
          <div className="dashboard-detail-item">
            <div className="dashboard-detail-item__label">ID:</div>
            <div className="dashboard-detail-item__value">{deal.id}</div>
          </div>

          <div className="dashboard-detail-item">
            <div className="dashboard-detail-item__label">Location</div>
            <div className="dashboard-detail-item__value">{deal.location}</div>
          </div>

          <div className="dashboard-detail-item">
            <div className="dashboard-detail-item__label">Transaction Type</div>
            <div className="dashboard-detail-item__value">
              {deal.transaction_type}
            </div>
          </div>

          <div className="dashboard-detail-item">
            <div className="dashboard-detail-item__label">Property Value</div>
            <div className="dashboard-detail-item__value">
              {deal.property_value}
            </div>
          </div>

          <div className="dashboard-detail-item">
            <div className="dashboard-detail-item__label">Commission:</div>
            <div className="dashboard-detail-item__value">{deal.commission}</div>
          </div>

          <div className="dashboard-detail-item">
            <div className="dashboard-detail-item__label">Property Type</div>
            <div className="dashboard-detail-item__value">
              {deal.property_type}
            </div>
          </div>

          <div className="dashboard-detail-item">
            <div className="dashboard-detail-item__label">Date Created</div>
            <div className="dashboard-detail-item__value">
              {deal.date_created}
            </div>
          </div>

          <div className="dashboard-detail-item">
            <div className="dashboard-detail-item__label">Deal Status</div>
            <div className="dashboard-detail-item__value">
              <span className="badge badge--success">{deal.status}</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

